import React, { useState, useEffect } from 'react';
import { unitsOfMeasureApi, outcomeDomainsApi, adminApi } from '../utils/api';
import Layout from '../components/Layout';
import SearchableSelect from '../components/SearchableSelect';

// PRD-027 WU-E — Units of Measure is no longer managed here; its single
// canonical home is Configure → Units of Measure (the Cost Types page UoM tab).
const TABS = [
  { id: 'domains', label: 'Outcome Domains' },
  { id: 'reassign', label: 'Bulk Reassign' },
];

export default function AdminCenter() {
  const [activeTab, setActiveTab] = useState('domains');

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="page-header">Admin Center</h1>
          <p className="text-gray-600 mt-1">Manage global picklists and data consolidation</p>
        </div>

        {/* Tab navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-8">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-600 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {activeTab === 'domains' && <OutcomeDomainsTab />}
        {activeTab === 'reassign' && <BulkReassignTab />}
      </div>
    </Layout>
  );
}

// ============================================================
// Outcome Domains
// ============================================================

function OutcomeDomainsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formName, setFormName] = useState('');
  const [saving, setSaving] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      const res = await outcomeDomainsApi.list({ includeInactive: true });
      setItems(res.data.outcomeDomains);
    } catch (error) {
      console.error('Failed to fetch outcome domains:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingItem) {
        await outcomeDomainsApi.update(editingItem.id, { name: formName });
      } else {
        await outcomeDomainsApi.create({ name: formName });
      }
      setShowModal(false);
      setEditingItem(null);
      setFormName('');
      await fetchItems();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Deactivate this outcome domain?')) return;
    try {
      await outcomeDomainsApi.delete(id);
      await fetchItems();
    } catch (error) {
      alert('Failed to deactivate');
    }
  };

  const handleReactivate = async (id) => {
    try {
      await outcomeDomainsApi.update(id, { isActive: true });
      await fetchItems();
    } catch (error) {
      alert('Failed to reactivate');
    }
  };

  const activeItems = items.filter(i => i.is_active);
  const inactiveItems = items.filter(i => !i.is_active);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">{activeItems.length} active outcome domains</p>
        <button
          onClick={() => { setShowModal(true); setEditingItem(null); setFormName(''); }}
          className="btn-primary text-sm"
        >
          + Add Outcome Domain
        </button>
      </div>

      {activeItems.length > 0 && (
        <div className="card overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase">Name</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {activeItems.map(item => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-900">{item.name}</td>
                  <td className="px-6 py-4 text-right space-x-3">
                    <button
                      onClick={() => { setEditingItem(item); setFormName(item.name); setShowModal(true); }}
                      className="text-xs text-primary-600 hover:text-primary-700"
                    >Edit</button>
                    <button
                      onClick={() => handleDeactivate(item.id)}
                      className="text-xs text-red-600 hover:text-red-700"
                    >Deactivate</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {inactiveItems.length > 0 && (
        <div>
          <button
            onClick={() => setShowInactive(!showInactive)}
            className="text-sm text-gray-600 hover:text-gray-700 flex items-center gap-1"
          >
            <svg className={`w-4 h-4 transition-transform ${showInactive ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
            {inactiveItems.length} inactive
          </button>
          {showInactive && (
            <div className="card overflow-hidden mt-2 opacity-60">
              <table className="min-w-full divide-y divide-gray-200">
                <tbody className="bg-white divide-y divide-gray-200">
                  {inactiveItems.map(item => (
                    <tr key={item.id}>
                      <td className="px-6 py-3 text-sm text-gray-600">{item.name}</td>
                      <td className="px-6 py-3 text-right">
                        <button onClick={() => handleReactivate(item.id)}
                          className="text-xs text-primary-600 hover:text-primary-700">Reactivate</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showModal && (
        <PicklistModal
          title={editingItem ? 'Edit Outcome Domain' : 'Add Outcome Domain'}
          value={formName}
          onChange={setFormName}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditingItem(null); setFormName(''); }}
          saving={saving}
          placeholder='e.g., "legal", "marketing", "data"'
        />
      )}
    </div>
  );
}

// ============================================================
// Tab 3: Bulk Reassign
// ============================================================

function BulkReassignTab() {
  return (
    <div className="space-y-8">
      <ReassignSection
        title="Reassign Units of Measure"
        description="Move all references from one unit of measure to another, then deactivate the source."
        fetchOptions={() => unitsOfMeasureApi.list({ includeInactive: false }).then(r => r.data.unitsOfMeasure)}
        previewFn={adminApi.previewUomReassign}
        executeFn={adminApi.executeUomReassign}
        entityLabel="unit of measure"
        renderAffected={(affected) => (
          <ul className="space-y-1 text-sm text-gray-600">
            <li>Outcome Types: <span className="font-medium">{affected.outcomeTypes}</span></li>
            <li>Proposal Outcomes: <span className="font-medium">{affected.proposalOutcomes}</span></li>
            <li>Cost Input Types: <span className="font-medium">{affected.costInputTypes}</span></li>
          </ul>
        )}
      />

      <ReassignSection
        title="Reassign Outcome Domains"
        description="Move all outcome types from one domain to another, then deactivate the source."
        fetchOptions={() => outcomeDomainsApi.list({ includeInactive: false }).then(r => r.data.outcomeDomains)}
        previewFn={adminApi.previewDomainReassign}
        executeFn={adminApi.executeDomainReassign}
        entityLabel="outcome domain"
        renderAffected={(affected) => (
          <ul className="space-y-1 text-sm text-gray-600">
            <li>Outcome Types: <span className="font-medium">{affected.outcomeTypes}</span></li>
          </ul>
        )}
      />
    </div>
  );
}

function ReassignSection({ title, description, fetchOptions, previewFn, executeFn, entityLabel, renderAffected }) {
  const [options, setOptions] = useState([]);
  const [sourceId, setSourceId] = useState(null);
  const [targetId, setTargetId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchOptions().then(setOptions).catch(() => {});
  }, [fetchOptions]);

  const handlePreview = async () => {
    if (!sourceId || !targetId) return;
    setLoading(true);
    setPreview(null);
    setResult(null);
    try {
      const res = await previewFn({ sourceId, targetId });
      setPreview(res.data);
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to preview');
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    if (!window.confirm(`This will reassign all references and deactivate the source ${entityLabel}. Continue?`)) return;
    setExecuting(true);
    try {
      const res = await executeFn({ sourceId, targetId });
      setResult(res.data);
      setPreview(null);
      setSourceId(null);
      setTargetId(null);
      // Refresh options
      const refreshed = await fetchOptions();
      setOptions(refreshed);
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to execute reassignment');
    } finally {
      setExecuting(false);
    }
  };

  const sourceOptions = options;
  const targetOptions = options.filter(o => o.id !== sourceId);

  return (
    <div className="card p-6">
      <h3 className="text-base font-semibold text-gray-900 mb-1">{title}</h3>
      <p className="text-sm text-gray-600 mb-4">{description}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="label">Source (will be deactivated)</label>
          <SearchableSelect
            options={sourceOptions}
            value={sourceId}
            onChange={(id) => { setSourceId(id); setPreview(null); setResult(null); if (id === targetId) setTargetId(null); }}
            placeholder={`Select source ${entityLabel}`}
          />
        </div>
        <div>
          <label className="label">Target (will receive references)</label>
          <SearchableSelect
            options={targetOptions}
            value={targetId}
            onChange={(id) => { setTargetId(id); setPreview(null); setResult(null); }}
            placeholder={`Select target ${entityLabel}`}
          />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={handlePreview}
          disabled={!sourceId || !targetId || loading}
          className="btn-secondary text-sm"
        >
          {loading ? 'Loading...' : 'Preview'}
        </button>
        {preview && (
          <button
            onClick={handleExecute}
            disabled={executing}
            className="btn-primary text-sm bg-red-600 hover:bg-red-700"
          >
            {executing ? 'Executing...' : 'Execute Reassignment'}
          </button>
        )}
      </div>

      {preview && (
        <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm font-medium text-yellow-800 mb-2">
            Reassign "{preview.source.name}" to "{preview.target.name}"
          </p>
          <p className="text-xs text-yellow-700 mb-2">Affected records:</p>
          {renderAffected(preview.affected)}
        </div>
      )}

      {result && (
        <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm font-medium text-green-800">Reassignment completed successfully.</p>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Shared Modal for picklist add/edit
// ============================================================

function PicklistModal({ title, value, onChange, onSave, onClose, saving, placeholder }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
        <form onSubmit={onSave} className="space-y-4">
          <div>
            <label className="label">Name *</label>
            <input
              type="text"
              required
              className="input"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder}
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
